import { useMemo } from 'react';

import { centsToDollar } from '@givebacks/utils/currency';
import { useElementSize } from '@mantine/hooks';

import { ButtonProps } from '../../../../Buttons';
import { Card } from '../../../../DataDisplay';
import { ImageMantine } from '../../../../DataDisplay/Image';
import { AspectRatio } from '../../../../Layout';
import { Flex } from '../../../../Layout/Flex';
import { Group } from '../../../../Layout/Group';
import { Stack } from '../../../../Layout/Stack';
import { Anchor } from '../../../../Navigation/Anchor/Anchor';
import { LoadingOverlay } from '../../../../Overlays';
import { Text } from '../../../../Typography/Text/Text';
import classes from './Cart.module.css';
import { CartItemsStack } from './CartItemsStack';
import { CartProps } from './types';

const useCartItemActionsSizes = (width: number) => {
  const isLargeCartCard = width >= 910;

  const cartItemActionsSize: ButtonProps['size'] = useMemo(() => {
    if (isLargeCartCard) {
      return 'sm';
    }
    return 'xs';
  }, [isLargeCartCard]);
  return { cartItemActionsSize };
};

type CheckoutProps = Pick<CartProps, 'subtotal' | 'checkOutButton'>;

const Checkout = ({ subtotal, checkOutButton }: CheckoutProps) => {
  return (
    <Stack
      className={classes.checkout}
      mod={{
        'with-checkout-button': Boolean(checkOutButton),
      }}
    >
      <Group justify="space-between">
        <Text fz="sm">Subtotal</Text>
        <Text fz="sm" fw={600}>
          {centsToDollar(subtotal)}
        </Text>
      </Group>
      {checkOutButton}
    </Stack>
  );
};

const TERMS_OF_SERVICE_URL = 'https://www.givebacks.com/terms';
const DONATION_DISBURSEMENT_URL =
  'https://support.givebacks.com/hc/en-us/articles/28961465884567-Donation-Disbursements';

const CartCard = ({
  cartItems,
  subtotal,
  storeData: { name: causeName, imageSrc: causeImg, shortener: causeShortener },
  showGivingaDisclaimer = false,
  /**
   * Slot for the checkout button, if provided, the cart content will be displayed in a column
   * otherwise it will be displayed in a row until responsive breakpoint
   */
  checkOutButton,
  cartItemProps,
  isLoading = false,
  onQuantityChange,
  isRecurring,
  cart,
  promotions,
  onCartItemNameClick,
  ...props
}: CartProps) => {
  const { ref, width } = useElementSize();
  const { cartItemActionsSize } = useCartItemActionsSizes(width);

  return (
    <Card className={classes.card} p={0} {...props} ref={ref}>
      <Stack gap={2} h="100%">
        <Group gap="xs" p="md" bg="white" align="flex-start">
          <AspectRatio className={classes.cause_image}>
            <ImageMantine src={causeImg} />
          </AspectRatio>
          <Stack gap={4} align="flex-start" flex={1}>
            <Text className={classes.cause_name} fw={500} style={{ wordBreak: 'break-word' }}>
              {causeName}
            </Text>
            {showGivingaDisclaimer && (
              <Text fz="xs" c="gray">
                <Text span fw={500} fz="xs">
                  {causeName}
                </Text>{' '}
                is an unenrolled charity. Givinga Foundation will receive your donation and grant
                the funds to your selected organization.{' '}
                <Anchor fw={500} fz="xs" href={TERMS_OF_SERVICE_URL} target="_blank">
                  See Terms of Service.
                </Anchor>{' '}
                <Anchor fw={500} fz="xs" href={DONATION_DISBURSEMENT_URL} target="_blank">
                  Learn more{' '}
                </Anchor>{' '}
                about enrolled recipient charities.
              </Text>
            )}
          </Stack>
        </Group>

        <Flex className={classes.cart_content}>
          <CartItemsStack
            cartItems={cartItems}
            cartItemProps={cartItemProps}
            onQuantityChange={onQuantityChange}
            onCartItemNameClick={onCartItemNameClick}
            actionsSize={cartItemActionsSize}
            isRecurring={isRecurring}
            cart={cart}
            promotions={promotions}
            cause={{ uuid: cart.cause_id, shortener: causeShortener }}
          />

          <Checkout subtotal={subtotal} checkOutButton={checkOutButton} />
        </Flex>
      </Stack>
      <LoadingOverlay visible={isLoading} />
    </Card>
  );
};

export { CartCard };
